import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { PageHeader } from '../components/ui/PageHeader';
import { Select } from '../components/ui/Select';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { SkeletonList } from '../components/ui/Skeleton';
import { api } from '../lib/api';
import {
  bautagebuchApi,
  type BautagebuchBueroEintrag,
  type BautagebuchFotoMeta,
} from '../lib/bautagebuch-api';
import { requireAccess } from '../lib/guards';
import { formatGermanDate, formatGermanDateTime } from '../lib/date-format';

export const Route = createFileRoute('/bautagebuch/')({
  beforeLoad: () => requireAccess('/api/v1/bautagebuch'),
  component: BautagebuchIndexPage,
});

interface SubRef {
  id: string;
  firmenname: string;
}

export function BautagebuchIndexPage(): JSX.Element {
  const [subId, setSubId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const filter = useMemo(
    () => ({
      ...(subId ? { subunternehmerId: subId } : {}),
      ...(dateFrom ? { eintragDatumVon: dateFrom } : {}),
      ...(dateTo ? { eintragDatumBis: dateTo } : {}),
    }),
    [subId, dateFrom, dateTo],
  );

  const subsQ = useQuery({
    queryKey: ['subunternehmer', 'list'],
    queryFn: () => api<{ items: SubRef[] }>('/api/v1/subunternehmer'),
  });

  const listQ = useQuery({
    queryKey: ['bautagebuch', 'list', filter],
    queryFn: () => bautagebuchApi.list(filter),
  });

  const items = listQ.data?.items ?? [];
  const hasFilter = subId !== '' || dateFrom !== '' || dateTo !== '';

  function resetFilter(): void {
    setSubId('');
    setDateFrom('');
    setDateTo('');
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Bautagebuch"
        description="Tagestagebuch-Einträge aller Subs und Aufträge — read-only Büro-Sicht."
      />

      {listQ.error ? <ErrorBanner error={listQ.error as Error} /> : null}

      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-white p-4">
        <label className="min-w-[220px] flex-1">
          <span className="mb-1 block text-sm font-medium text-slate-700">Subunternehmer</span>
          <Select value={subId} onChange={(e) => setSubId(e.target.value)}>
            <option value="">Alle</option>
            {(subsQ.data?.items ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.firmenname}
              </option>
            ))}
          </Select>
        </label>
        <label className="min-w-[160px]">
          <span className="mb-1 block text-sm font-medium text-slate-700">Von</span>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </label>
        <label className="min-w-[160px]">
          <span className="mb-1 block text-sm font-medium text-slate-700">Bis</span>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </label>
        {hasFilter ? (
          <Button variant="ghost" onClick={resetFilter}>
            Filter zurücksetzen
          </Button>
        ) : null}
      </div>

      {listQ.isLoading ? (
        <SkeletonList />
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
          {hasFilter
            ? 'Keine Einträge in dieser Auswahl.'
            : 'Noch keine Bautagebuch-Einträge erfasst.'}
        </div>
      ) : (
        <ul className="space-y-3">
          {items.map((e) => (
            <EintragCard key={e.id} eintrag={e} />
          ))}
        </ul>
      )}
    </div>
  );
}

function EintragCard({ eintrag }: { eintrag: BautagebuchBueroEintrag }): JSX.Element {
  const datum = formatGermanDate(eintrag.eintragDatum);
  const erstellt = formatGermanDateTime(eintrag.createdAt);
  return (
    <li className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-slate-900">
            {datum} ·{' '}
            <Link
              to="/auftraege/$id"
              params={{ id: eintrag.auftragId }}
              className="text-brand-700 hover:underline"
            >
              {eintrag.auftragTitel}
            </Link>
          </div>
          <div className="mt-1 text-xs text-slate-500">
            {eintrag.authorDisplayName}
            {eintrag.subunternehmerFirmenname ? ` · ${eintrag.subunternehmerFirmenname}` : ''} ·
            erfasst {erstellt}
          </div>
        </div>
      </div>
      {eintrag.text.length > 0 ? (
        <p className="mt-3 whitespace-pre-line text-sm text-slate-800">{eintrag.text}</p>
      ) : null}
      {eintrag.fotos.length > 0 ? <FotoGrid fotos={eintrag.fotos} /> : null}
    </li>
  );
}

function FotoGrid({ fotos }: { fotos: BautagebuchFotoMeta[] }): JSX.Element {
  return (
    <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
      {fotos.map((f) => {
        const url = bautagebuchApi.fotoUrl(f.id);
        return (
          <a
            key={f.id}
            href={url}
            target="_blank"
            rel="noreferrer"
            className="block overflow-hidden rounded-md border border-slate-200 bg-slate-50 hover:border-slate-300"
            title={f.originalFilename}
          >
            <img
              src={url}
              alt={f.originalFilename}
              loading="lazy"
              className="h-32 w-full object-cover"
            />
          </a>
        );
      })}
    </div>
  );
}
